/**
 * **Who is before and after this client on the trainer's list** — the top bar's
 * `‹ Poprzednia / Następna ›` pair, KANON `Detal klienta v3`.
 *
 * ── The order is the ROSTER's, reproduced rather than invented ─────────────────────────────────
 * The roster sorts four ways and filters two more, all client-side, and "the next client" means the
 * next one on *that* list. So this projection does not have an ordering of its own: it runs
 * `rosterView` — the same call the roster's own table renders from — over the same rows, with the
 * context the address carried. Anything else would put two different meanings behind one pair of
 * arrows, and the trainer would find out by pressing one.
 *
 * ── An OFFER is not a stop on the walk ────────────────────────────────────────────────────────
 * An invitation to somebody who is not yet this trainer's client has no `customerId`, so there is
 * no client detail to open. The roster already refuses to draw it as a link for that reason; the
 * arrows skip it for the same one, rather than stepping onto an address that can only 404. An
 * invited roster ENTRY carries its id and is a stop like any client (R2). The rows are filtered AFTER ordering, so the
 * survivors keep the roster's own sequence.
 *
 * ── The ends are ENDS ─────────────────────────────────────────────────────────────────────────
 * The first client's `‹` and the last client's `›` are refused, never wrapped. A wrap would make
 * the two controls a carousel with no way to tell that the list had restarted — and a trainer
 * working down a list of people who need attention would silently begin again at the top.
 *
 * ── Absence is told apart from an end ─────────────────────────────────────────────────────────
 * `onList` is false while the roster read is in flight, after it failed, and where this client is
 * not on the list the address describes at all (a filter that excludes them, a search that does not
 * match). All three refuse both arrows, and the caller says WHY rather than drawing two dead
 * controls with no reason attached.
 */

import { rosterView, type ClientRow, type RosterContext } from '~/entities/client';

/** A client the arrows can step onto: one that exists and can be opened. */
export interface ClientNeighbour {
  readonly clientId: number;
  readonly name: string;
}

export interface ClientNeighbours {
  readonly previous: ClientNeighbour | null;
  readonly next: ClientNeighbour | null;
  /**
   * Is this client on the list the address describes?
   *
   * False separates "there is no list" and "this client is not on it" from "this client is at the
   * end of it" — the first two refuse both arrows for a reason worth saying, the third refuses one
   * for a reason the trainer can already see.
   */
  readonly onList: boolean;
}

const NOWHERE: ClientNeighbours = {
  previous: null,
  next: null,
  onList: false,
};

/**
 * @param rows every client the trainer has, or `null` while that read is in flight or after it
 *   failed. Unordered — the ordering is applied here.
 * @param clientId the client whose detail is open.
 * @param context the list the address describes, and the clock the "lapsed" tab is read against.
 */
export function clientNeighbours(
  rows: readonly ClientRow[] | null,
  clientId: number,
  context: RosterContext & { readonly locale: string; readonly now: Date },
): ClientNeighbours {
  if (rows === null) return NOWHERE;

  const walk = rosterView(rows, context).flatMap((row) =>
    row.customerId === null ? [] : [{ clientId: row.customerId, name: row.fullName }],
  );

  const at = walk.findIndex((row) => row.clientId === clientId);
  if (at === -1) return NOWHERE;

  return {
    previous: walk[at - 1] ?? null,
    next: walk[at + 1] ?? null,
    onList: true,
  };
}
